import { MessageSquare, Sparkles, Radio } from 'lucide-react';

export type ConversationMode = 'discovery' | 'infer' | 'watch';

interface ModeToggleProps {
  mode: ConversationMode;
  onChange: (mode: ConversationMode) => void;
  disabled?: boolean;
  compact?: boolean;
}

const modeConfig: Record<ConversationMode, {
  label: string;
  subtitle: string;
  icon: typeof MessageSquare;
  colorClass: string;
}> = {
  discovery: {
    label: 'Discovery',
    subtitle: 'Step by step',
    icon: MessageSquare,
    colorClass: 'ap-mode--discovery',
  },
  infer: {
    label: 'Infer',
    subtitle: 'AI generates',
    icon: Sparkles,
    colorClass: 'ap-mode--infer',
  },
  watch: {
    label: 'Watch',
    subtitle: 'Listen only',
    icon: Radio,
    colorClass: 'ap-mode--watch',
  },
};

const modes: ConversationMode[] = ['discovery', 'watch'];

export default function ModeToggle({ mode, onChange, disabled = false, compact = false }: ModeToggleProps) {
  // Compact: show only the active mode as a small badge (for the header when session is active)
  if (compact) {
    const { label, icon: Icon, colorClass } = modeConfig[mode];
    return (
      <div className={`ap-mode-badge ${colorClass}`}>
        <Icon size={12} />
        <span>{label}</span>
      </div>
    );
  }

  return (
    <div className={`ap-mode-toggle ${disabled ? 'ap-mode-toggle--disabled' : ''}`}>
      {modes.map((m) => {
        const { label, subtitle, icon: Icon, colorClass } = modeConfig[m];
        const isActive = m === mode;

        return (
          <button
            key={m}
            type="button"
            className={`ap-mode-pill ${isActive ? `ap-mode-pill--active ${colorClass}` : ''}`}
            disabled={disabled}
            onClick={() => {
              if (!disabled) onChange(m);
            }}
            aria-pressed={isActive}
          >
            <span className="ap-mode-pill__icon-row">
              <Icon size={14} />
              <span className="ap-mode-pill__label">{label}</span>
            </span>
            <span className="ap-mode-pill__subtitle">{subtitle}</span>
          </button>
        );
      })}
    </div>
  );
}
